import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { SizesQuizStepId } from '../sizes-quiz.data'

/**
 * "Find my sizes" — open / close. Values go to Sizes as they are entered (R109);
 * only the step to come back to is local.
 */
const STORAGE_KEY = 'fair.sizes-quiz'

interface SizesQuizSaved {
  step?: SizesQuizStepId
  withAdditional?: boolean
}

function readSaved(): SizesQuizSaved {
  if (typeof window === 'undefined') return {}
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}') as SizesQuizSaved
  } catch {
    return {}
  }
}

function writeSaved(patch: SizesQuizSaved) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readSaved(), ...patch }))
  } catch {
    // Private mode / quota — the quiz just starts over next time.
  }
}

export const useSizesQuizStore = defineStore('sizesQuiz', () => {
  const isOpen = ref(false)
  /** Bumped on every open so the quiz re-reads the profile. */
  const session = ref(0)

  function open() {
    session.value += 1
    isOpen.value = true
  }

  function close() {
    isOpen.value = false
  }

  function saved(): SizesQuizSaved {
    return readSaved()
  }

  function save(step: SizesQuizStepId, withAdditional: boolean) {
    // Done is not a place to come back to.
    writeSaved(step === 'done' ? { step: undefined, withAdditional: false } : { step, withAdditional })
  }

  return { isOpen, session, open, close, saved, save }
})
